/**
 * The Conversions API copy of a pixel event (108O): posted to our own Worker,
 * which sends it to Meta with the store's token when the store has the copies
 * on. Fire-and-forget — a lost copy only means the browser event stands alone.
 */
import { CAPI_FORM_EVENTS, isCapiBrowserEvent, type CapiBeacon, type CapiFormUser } from "@shared/capiBeacon";
import { getFbBrowserParams } from "./fbParams";
import type { PixelUserData } from "./pixel";

const URL_CAPI = "/api/public/capi";
const OFF_KEY = "shz_capi_off";

/** Pixels the Worker said have no copies — not posted again this session. */
const off = new Set<string>(readOff());

function readOff(): string[] {
  try {
    return JSON.parse(window.sessionStorage.getItem(OFF_KEY) ?? "[]") as string[];
  } catch {
    return [];
  }
}

function markOff(pixelId: string): void {
  off.add(pixelId);
  try {
    window.sessionStorage.setItem(OFF_KEY, JSON.stringify([...off]));
  } catch {
    /* private mode: re-learnt on the next page */
  }
}

function formUser(u: PixelUserData | undefined): CapiFormUser | undefined {
  if (!u) return undefined;
  const out: CapiFormUser = {};
  for (const k of ["em", "ph", "fn", "ln", "ct", "st", "zp", "country"] as const) {
    const v = u[k]?.trim();
    if (v) out[k] = v;
  }
  return Object.keys(out).length ? out : undefined;
}

export function sendCapiCopy(
  pixelId: string,
  event: string,
  eventId: string,
  params?: Record<string, unknown>,
  userData?: PixelUserData,
): void {
  if (!isCapiBrowserEvent(event) || off.has(pixelId)) return;
  // Never in the way of the pixel event itself: anything thrown here is dropped.
  try {
    const fb = getFbBrowserParams();
    const user = CAPI_FORM_EVENTS.includes(event) ? formUser(userData) : undefined;
    const beacon: CapiBeacon = {
      px: pixelId,
      ev: event,
      id: eventId,
      url: window.location.href,
      ...(params && Object.keys(params).length ? { cd: params } : {}),
      ...(user ? { ud: user } : {}),
      ...(fb.fbp ? { fbp: fb.fbp } : {}),
      ...(fb.fbc ? { fbc: fb.fbc } : {}),
      ...(fb.externalId ? { xid: fb.externalId } : {}),
    };
    fetch(URL_CAPI, {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(beacon),
    })
      .then((r) => (r.status === 200 ? r.json() : null))
      .then((j: { on?: boolean } | null) => {
        if (j?.on === false) markOff(pixelId);
      })
      .catch(() => {});
  } catch {
    /* the browser event stands alone */
  }
}
